import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import { MoveParticipant } from "@remo-co/ui-core/src/icons/MoveParticipant";
import { useI18n } from "i18n";
import { useAppDispatch } from "store/hooks";
import {
  resetUserForceMove,
  setShowUserForceMovePopup,
} from "modules/userForceMove/redux/slice";
import {
  deleteEventUserNotification,
  getUserForceMoveCountDown,
} from "services/firebaseService/eventUserNotifications";
import { useSelector } from "react-redux";
import {
  selectUserForceMoveData,
  selectDelayThrottleFunction,
} from "modules/userForceMove/redux/selectors";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  BUFFER_COUNT_DOWN,
  LINEAR_PROGRESS_INCREASE,
} from "modules/userForceMove/constants";
import { selectUserId } from "modules/auth/redux/selectors";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { selectCurrentEventId } from "modules/event/selectors";
import { useFindParticipant } from "modules/audioVideo/hooks/useParticipant/findParticipant";
import { LinearProgress } from "@remo-co/ui-core/src/components/LinearProgress";
import { selectCurrentTheaterId } from "modules/theater/selectors";
import { useFloorNames } from "modules/floors/hooks/useFloorNames";
import { useStyles } from "./styles";

export const UserForceMoveCancel = () => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["conversation", "common"]);
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const userForceMoveData = useSelector(selectUserForceMoveData);
  const delayThrottleFunction = useSelector(selectDelayThrottleFunction);
  const eventId = useSelector(selectCurrentEventId);
  const currentTheaterId = useSelector(selectCurrentTheaterId);
  const floorNames = useFloorNames(currentTheaterId);
  const currentUserId = useSelector(selectUserId);
  const increaseValue = LINEAR_PROGRESS_INCREASE / 10; // dividing by 10 to add the value every 100ms
  const { findParticipant } = useFindParticipant();

  const initialProgress = useMemo(() => {
    if (userForceMoveData) {
      const diff =
        BUFFER_COUNT_DOWN -
        getUserForceMoveCountDown(userForceMoveData.createdAt);
      return diff + diff * increaseValue;
    }

    return 0;
  }, [userForceMoveData, increaseValue]);

  const [progress, setProgress] = useState(initialProgress);

  const handleCancelMove = () => {
    deleteEventUserNotification();
    delayThrottleFunction?.cancel();
    dispatch(resetUserForceMove());

    track(Events.MOVE_USER_CANCELLED, {
      eventId,
      userId: userForceMoveData?.userId,
    });
  };

  const handlePopupClose = () => {
    dispatch(setShowUserForceMovePopup(false));
  };

  const onCompleteProgress = useCallback(() => {
    const participant = findParticipant(userForceMoveData?.userId || "");

    if (!participant) {
      deleteEventUserNotification();
      dispatch(resetUserForceMove());
    }

    dispatch(setShowUserForceMovePopup(false));
  }, [dispatch, findParticipant, userForceMoveData]);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress === 100) {
          onCompleteProgress();
          return 0;
        }

        return Math.min(prevProgress + increaseValue, 100);
      });
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, [dispatch, increaseValue, onCompleteProgress]);

  if (!userForceMoveData || userForceMoveData?.managerId !== currentUserId) {
    return null;
  }

  return (
    <Container className={styles.container} data-testid="user-move-cancel">
      <Box sx={{ width: "100%" }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          className={styles.linearProgress}
        />
      </Box>
      <Box className={styles.messageContent}>
        <Box className={styles.messageBox}>
          <MoveParticipant className={styles.icon} />
          <Typography className={styles.message}>
            {t("conversation:force.move.host.message", {
              name: userForceMoveData.userName,
              floorName:
                floorNames[userForceMoveData.targetFloorId] ||
                userForceMoveData.floorName,
              tableName: userForceMoveData.tableName,
            })}
          </Typography>
          <div>
            <IconButtonWithTooltip
              id="popup-close"
              title={t("common:button.close")}
              size="small"
              className={styles.closeButton}
              onClick={handlePopupClose}
              data-testid="close-button"
            >
              <CloseIcon />
            </IconButtonWithTooltip>
          </div>
        </Box>
        <Box className={styles.cancelBox}>
          <Button
            color="dark"
            size="sm"
            variant="secondary"
            className={styles.cancelButton}
            onClick={handleCancelMove}
            data-testid="cancel-move-button"
          >
            <Typography variant="caption">
              {t("conversation:cancel.move")}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
